import { createAction, props } from '@ngrx/store';
import { AuditTrailEntry, CommitRequest, SaveStudyRequest, Study } from '../../shared/models/study.model';
import { CalAdjustedDisplayOrder, FileMetaData, ReferenceFileInfoResponse, ReportContentItem, ReportContentItemResponse, RunSampleLot, Sample } from '../../shared/models/run.model';
import { CalculationRequest } from '../../shared/models/calculate.model';
import { CalLot, MeanBiasAndSlope } from '../../shared/models/cal-lot.model';
import { QcLot } from '../../shared/models/qc-lot.model';
import { AdjustedCalLots, CalculationResponse, SampleSummaryResult } from '../../shared/models/result.model';
import { Panel } from '../../shared/models/assays.model';
import { CoaDetails } from '../../shared/models/coa-details.model';
import { ProcessingActivity } from './store';
import { CalAdjustmentMode } from '../../shared/utils/run-type.util';
import { CalibratorCoaDetails } from '../coa-module/services/calibrator-value-assignment-coa.builder';
import { ControlCoaDetails } from '../coa-module/services/control-value-assignment-coa.builder';

/**
 * Action to set the authentication status, the user, and optional app version metadata.
 * @param isAuthenticated Boolean flag indicating authentication status.
 * @param user The authenticated user's username.
 * @param appVersion The BPX app version reported by the backend, if available.
 */
export const setIsAuthenticated = createAction('[Authentication] Set', props<{ isAuthenticated: boolean, user: string, userEmail?: string, appVersion?: string, isSuperUser?: boolean }>());

/**
 * Action to set the authentication token.
 * @param authToken The authentication token for the user.
 */
export const setAuthToken = createAction('[AuthToken] Set', props<{ authToken: string }>());
/**
 * Action to reset the authentication state.
 */
export const resetAuthentication = createAction('[Authentication] Reset');
/**
 * Action to set the selected study.
 * @param selectedStudy The study object to set.
 */
export const setStudy = createAction('[Set Study] Set', props<{ selectedStudy: Study }>());
/**
 * Action to reset the current study.
 */
export const resetStudy = createAction('[Reset Study] Reset');
/**
 * Action to set the run setup with sample and report content information.
 * @param runSamples List of samples in the run.
 * @param reportContentItemResponse Report content item response object.
 */
export const setRunSetup = createAction('[Set Run Setup] Set', props<{ runSamples: Sample[], reportContentItemResponse: ReportContentItemResponse, comments: string, testSpec: string, assignSpec: string, refLot: string, calculateAverages: boolean, useLegacy4pl: boolean, optimizeAdjustment: boolean, skipMissingSamples: boolean }>());
/**
 * Action to set patient file metadata along with kit lots, assay names, and bead regions.
 * @param patientFileMetadata The list of patient file metadata.
 * @param kitLots The list of kit lots.
 * @param assayNames The list of assay names.
 * @param beadRegions The list of bead regions.
 */
export const setpatientFileMetadata = createAction('[Set Patient File Meta Data] Set', props<{ patientFileMetadata: FileMetaData[], kitLots: string[] }>());
/**
 * Action to set calibration file metadata.
 * @param calFileMetadata The list of calibration file metadata.
 */
export const setcalFileMetadata = createAction('[Set Cal File Meta Data] Set', props<{ calFileMetadata: FileMetaData[], kitLots: string[] }>());
/**
 * Action to set control file metadata.
 * @param controlFileMetadata The list of control file metadata.
 */
export const setcontrolFileMetadata = createAction('[Set Control File Meta Data] Set', props<{ controlFileMetadata: FileMetaData[], kitLots: string[] }>());
/**
 * Action to set the number of assay rows to seed per selected kit lot (RMQC run type only).
 * @param rmqcAssayCount The number of assay rows to create for each selected kit lot.
 */
export const setRmqcAssayCount = createAction('[Set Rmqc Assay Count] Set', props<{ rmqcAssayCount: number }>());
export const setRunSamples = createAction('[Set Run Samples] Set', props<{ runSampleLots: RunSampleLot[] }>());

/**
 * Action to set panel data including name, run type, specs, and report content items.
 * @param panelName The name of the panel.
 * @param runType The type of the run.
 * @param panelSpecs The specifications for the panel.
 * @param reportContentItems The report content items associated with the panel.
 * @param isRestore Set when replaying a saved study on load, so the study is not marked as edited.
 */
export const setPanel = createAction('[Set Panel] Set', props < { panelName: string, runType: string, panelSpecs: Panel | null, reportContentItems: ReportContentItemResponse, assayNames: string[], beadRegions: number[], isRestore?: boolean }>());
/**
 * Incrementally update Report Items by sending only the changed rows and (optionally) updated variable column metadata.
 * This keeps NgRx actions small so that Run Info tabs no longer need to serialize hundreds of rows when a single cell changes.
 */
export const patchReportContentItems = createAction(
  '[Report Content Items] Patch Rows',
  props<{ changedItems: ReportContentItem[]; variableColumns?: string[]; removedItemIds?: string[] }>()
);
/**
 * Action to reset all state.
 */
export const resetAll = createAction('[Reset All] Reset');
/**
 * Action to run a calculation.
 * @param calculationRequest The request object containing calculation data.
 */
export const runCalculation = createAction('[Calculation] Run Calculation', props<{ calculationRequest: CalculationRequest }>());
/**
 * Action to run a calculation and export the results as CSV files
 * @param calculationRequest The request object containing calculation data.
 */
export const runGetCalculationCsv = createAction('[Calculation] Run Get Calculation Csv', props<{ calculationRequest: CalculationRequest }>());
/**
 * Action to run a calculation and export the results as CSV files
 * @param calculationRequest The request object containing calculation data.
 */
export const runGetCalculationCsvSuccess = createAction('[Calculation] Run Get Calculation Csv Success', props<{ calculationResponse: Blob, fileName: string }>())
/**
 * Action to handle successful calculation execution.
 * @param calculationResponse The response object containing calculation results.
 * @param calAdjustmentMode Calibrator Adjustment only: the mode the request ran in.
 * @param finalValueAssignment Final mode only: the value assignments the request finalized.
 */
export const runCalculationSuccess = createAction(
  '[Calculation] Run Calculation Success',
  props<{ calculationResponse: CalculationResponse; calAdjustmentMode?: CalAdjustmentMode; finalValueAssignment?: number[][] }>()
);
/**
 * Action to handle error in calculation.
 * @param error The error object from the failed calculation.
 */
export const runCalculationError = createAction('[Calculation] Run Calculation Error', props<{ error: Error }>());
/**
 * Action to store the most recent calculation cache ID and type.
 * @param calculationId The unique calculation ID returned by the backend.
 * @param calculationType Whether the calculation was adjusted or unadjusted.
 */
export const setLastCalculationCache = createAction(
  '[Calculation] Set Last Cache',
  props<{ calculationId: string; calculationType: 'adjusted' | 'unadjusted'; clearUnadjusted?: boolean }>()
);

/**
 * Load stored sample data for one or more report content items.
 */
export const ensureSampleData = createAction('[Sample Data] Ensure Fresh Data');
export const beginSampleDataLoad = createAction('[Sample Data] Begin Load');

export const loadSampleData = createAction('[Sample Data] Load', props<{ reportContentItemIds: string[]; failedOnly: boolean }>());

/**
 * Store sample data retrieved from the pull_sample_data endpoint.
 */
export const loadSampleDataSuccess = createAction('[Sample Data] Load Success', props<{ failedOnly: boolean; unadjustedSamples: SampleSummaryResult[]; adjustedSamples: SampleSummaryResult[] }>());

/**
 * Track failures when loading sample data.
 */
export const loadSampleDataFailure = createAction('[Sample Data] Load Failure', props<{ failedOnly: boolean; error: Error }>());
/**
 * Action to load a calibration lot by its number.
 * @param lotNumber The lot number of the calibration lot.
 */
export const loadCalLot = createAction('[Cal Lot] Load Cal Lot', props<{ lotNumber: string, specRevision: string, assignmentVersion?: number | null, isRestore?: boolean }>());
/**
 * Action to handle successful calibration lot loading.
 * @param calLot The loaded calibration lot object.
 * @param isRestore Set when the lot is being rehydrated on draft load, so the study is not marked as edited.
 */
export const loadCalLotSuccess = createAction('[Cal Lot] Load Cal Lot Success', props<{ calLot: CalLot, isRestore?: boolean }>());

/**
 * Action to handle error in loading calibration lot.
 * @param error The error object from the failed lot loading.
 */
export const loadCalLotError = createAction('[Cal Lot] Load Cal Lot Error', props<{ error: Error }>());
/**
 * Action to remove a calibration lot by its number.
 * @param lotNumber The lot number of the calibration lot.
 */
export const deleteCalLot = createAction('[Cal Lot] Delete', props<{ lotNumber: string, assignmentVersion?: number | null }>());

/**
 * Action to set the mean bias and slope values for the calibration lot.
 * @param meanBiasAndSlope The mean bias and slope data.
 */
export const setMeanBiasAndSlope = createAction('[Cal Lot] Set Mean Bias and Slope', props<{ meanBiasAndSlope: MeanBiasAndSlope }>());

/**
 * Action to load a QC lot by its number.
 * @param lotNumber The lot number of the QC lot.
 */
export const loadQcLot = createAction('[QC Lot] Load QC Lot', props<{ lotNumber: string, specRevision: string, assignmentVersion?: number | null, isRestore?: boolean }>());
/**
 * Action to handle successful QC lot loading.
 * @param qcLot The loaded QC lot object.
 * @param isRestore Set when the lot is being rehydrated on draft load, so the study is not marked as edited.
 */
export const loadQcLotSuccess = createAction('[QC Lot] Load QC Lot Success', props<{ qcLot: QcLot, isRestore?: boolean }>());
/**
 * Action to handle error in loading QC lot.
 * @param error The error object from the failed lot loading.
 */
export const loadQcLotError = createAction('[QC Lot] Load QC Lot Error', props<{ error: Error }>());
/**
 * Action to remove a QC lot by its number.
 * @param lotNumber The lot number of the QC lot.
 */
export const deleteQcLot = createAction('[QC Lot] Delete', props<{ lotNumber: string, assignmentVersion?: number | null }>());
/**
 * Action to set the selected calibration assay lot's run ID.
 * @param reportContentItemId The report content item ID related to the selected assay lot.
 */
export const setSelectedCalcAssayLotRunId = createAction('[Calc Items List] Selected Assay Lot RunId', props<{ reportContentItemId: string | null }>());
/**
 * Action to set the selected sample assay lot's run ID.
 * @param reportContentItemId The report content item ID related to the selected sample.
 */
export const setSelectedSampleAssayLotRunId = createAction('[Sample List] Selected Assay Lot RunId', props<{ reportContentItemId: string | null }>());
/** Action to switch Calculations > Sample into or out of Failed Samples mode (all report content items). */
export const setCalcFailedSamplesMode = createAction('[Calc Items List] Failed Samples Mode', props<{ enabled: boolean }>());
/** Action to switch Sample Analysis > List into or out of Failed Samples mode (all report content items). */
export const setSampleFailedSamplesMode = createAction('[Sample List] Failed Samples Mode', props<{ enabled: boolean }>());
/**
 * Action to set the selected control QC analysis assay lot's run ID.
 * @param reportContentItemId The report content item ID related to the selected control QC analysis lot.
 */
export const setSelectedControlQcAnalysisLotRunId = createAction('[Control Qc Analysis] Selected Assay Lot RunId', props<{ reportContentItemId: string | null }>());
/**
 * Action to set the selected control curve assay lot's run ID.
 * @param reportContentItemId The report content item ID related to the selected control curve lot.
 */
export const setSelectedControlCurveLotRunId = createAction('[Control Curve] Selected Assay Lot RunId', props<{ reportContentItemId: string | null }>());
/**
 * Action to set the selected assignment lot's run ID.
 * @param reportContentItemId The report content item ID related to the selected assignment lot.
 */
export const setSelectedAssignmentLotRunId = createAction('[Assignment] Selected Assignment Lot RunId', props<{ reportContentItemId: string | null }>());
/**
 * Action to set adjusted calibration lots.
 * @param adjustedCalLots The adjusted calibration lots along with original calibration lots and value assignments.
 * @param isInitialLoad Set when seeding the store as a tab opens, so rendering is not treated as an adjustment.
 */
export const setAdjustedCalLots = createAction('[Set Adjusted Cal Lots] Set', props<{ adjustedCalLots: { originalCalLot: AdjustedCalLots[], adjustedCalLot: AdjustedCalLots[], valueAssignment: number[][], originalValueAssignment?: number[][] }, isInitialLoad?: boolean }>());
/**
 * Records that the current adjusted calculation's server-computed assignments have been seeded into
 * the Adjusted Calibrator form, so a later rebuild of that form leaves manual adjustments alone.
 */
export const markAdjustedAssignmentSeeded = createAction(
  '[Adjusted Calibrator] Mark Adjusted Assignment Seeded'
);
/**
 * Operator's override of the Allowed adjustment factor (%) on Adjusted Calibrator.
 * `null` clears the override, so the screen falls back to the calibration administration default.
 */
export const setAllowedAdjustmentFactor = createAction(
  '[Cal Adjusted] Set Allowed Adjustment Factor',
  props<{ allowedAdjustmentFactor: number | null }>()
);
/**
 * The order to display adjusted calibration lots according to the user-defined Run Report Items table.
 */
export const setCalAdjustedDisplayOrder = createAction(
  '[Cal Adjusted] Set Display Order',
  props<{ dataTableOrder: CalAdjustedDisplayOrder }>()
);
/**
 * Action to reset the selected study and panel data.
 */
export const resetSelectedStudyAndPanel = createAction('[Reset] Reset Selected Study and Panel');

/**
 * Action to enable or disable calculation for adjusted values.
 * @param isCalculateAdjustedEnabled Boolean flag indicating whether calculation for adjusted values is enabled.
 */
export const setCalculateAdjustedEnabled = createAction('[Set Calculate Enabled on Adjusted Values] Set', props<{ isCalculateAdjustedEnabled: boolean }>());
/**
 * Action to set the active assay tab.
 * @param activeAssayTab The currently active assay tab.
 */
export const setActiveAssayTab = createAction('[Assays] Active Tab', props<{ activeAssayTab: string | null }>());
/**
 * Action to persist selected columns for the Assay Summary tab.
 * @param columnKeys Array of column keys representing the selected columns.
 */
export const setAssaySummaryColumnSelection = createAction(
  '[Assays] Summary Column Selection',
  props<{ columnKeys: string[] }>()
);
/**
 * Action to persist selected columns for the Assay Details tab.
 * @param columnKeys Array of column keys representing the selected columns.
 */
export const setAssayDetailsColumnSelection = createAction(
  '[Assays] Details Column Selection',
  props<{ columnKeys: string[] }>()
);
/**
 * Action to record the Certificate of Analysis facts entered on the C of A screen.
 *
 * They are saved with the study, so an edit marks the study as changed the same way any other
 * study edit does.
 *
 * @param coaDetails The certificate details as the screen now holds them.
 */
export const setCoaDetails = createAction(
  '[C of A] Set Details',
  props<{ coaDetails: CoaDetails }>()
);
/**
 * Action to remember the calibrator CoA dialog's entries for the rest of the session.
 * @param calibratorCoaDetails The entries the dialog closed with.
 */
export const setCalibratorCoaDetails = createAction(
  '[Assignment] Set Calibrator CoA Details',
  props<{ calibratorCoaDetails: CalibratorCoaDetails }>()
);
/**
 * Action to remember the control CoA dialog's entries for the rest of the session.
 * @param controlCoaDetails The entries the dialog closed with.
 */
export const setControlCoaDetails = createAction(
  '[Assignment] Set Control CoA Details',
  props<{ controlCoaDetails: ControlCoaDetails }>()
);
/**
 * Action to save the study data.
 * @param saveStudyRequest The request object to save the study.
 * @param autoLinkToStudyName On a Save-As, the study being saved from. The save payload already
 * links the two, but the draft endpoint ignores `related_study_names`, so the effect adds the
 * linkage itself when the save went that way.
 */
export const saveStudy = createAction(
  '[Save] Save Study',
  props<{ saveStudyRequest: SaveStudyRequest; autoLinkToStudyName?: string }>()
);
/**
 * Action to handle successful study save.
 * @param study The saved study object.
 */
export const saveStudySuccess = createAction('[Save] Save Study Success', props<{ studyName: string; studyId?: string | null; auditTrail?: AuditTrailEntry[]; isCommitted?: boolean }>());
/**
 * Action to handle error while saving the study.
 * @param error The error object from the failed save.
 */
export const saveStudyError = createAction('[Save] Save Study Error', props<{ error: Error }>());
/**
 * Action to set comments for the study or process.
 * @param comments The comments to set.
 */
export const setComments = createAction('[Comment] Set', props<{ comments: string }>());
/**
 * Action to set the panel reference for run data.
 * @param panelReference The reference object for the panel data.
 */
export const setRunDataPanels = createAction('[Run] Panel', props<{ panelReference: ReferenceFileInfoResponse | null }>());

/**
 * Action to commit the study.
 * @param commitStudyRequest The request object to commit the study.
 */
export const commitStudy = createAction('[Commit] Commit Study', props<{ commitStudyRequest: CommitRequest }>());

/**
 * Action to raise or clear the header's progress indicator.
 * @param isProcessing Whether a long-running operation is in flight.
 * @param activity Which operation it is, so the header can name it. Optional, and ignored when
 * clearing — every dispatch that sets the flag false is simply the end of whatever was running.
 */
export const setProcessingStatus = createAction(
  '[Set] Processing Status',
  props<{ isProcessing: boolean, activity?: ProcessingActivity }>()
);

/**
 * Action to set the load study error message (used for header status).
 * @param message Error message to surface, or null to clear.
 */
export const setLoadStudyError = createAction('[Load Study] Error', props<{ message: string | null }>());

export const setHasNewInstrumentUploads = createAction('[Instrument Upload] Study Has New Uploads', props<{ hasNewInstrumentUploads: boolean }>());

export const setAuditTrail = createAction('[Audit Trail] Set', props<{ auditTrail: AuditTrailEntry[] }>());
